/**
 * AR Quick Look launcher — the iOS/iPadOS path. Hands off to Apple's native ARKit viewer via an
 * `<a rel="ar">` link to a USDZ. ARKit owns tracking (LiDAR-grade 6DoF), so there is no canvas,
 * no getUserMedia, and no pose stream here. Branding lives baked INTO the USDZ; the user captures
 * and shares through Apple's native UI (we don't get the photo bytes back — see runQuickLook).
 *
 * No third-party code: this is just a platform anchor. Stays Apache-2.0.
 */

export interface QuickLookBanner {
  /** Banner title (Apple `checkoutTitle`). */
  title?: string;
  /** Banner subtitle (Apple `checkoutSubtitle`). */
  subtitle?: string;
  /** Custom action button label (Apple `callToAction`). */
  callToAction?: string;
  /** Where the action button / "open page" sends the user (Apple `canonicalWebPageURL`). */
  canonicalWebPageURL?: string;
}

/** True if the browser supports AR Quick Look (iOS/iPadOS Safari + in-app webviews). */
export function isQuickLookSupported(doc: Document = document): boolean {
  const anchor = doc.createElement("a") as HTMLAnchorElement;
  return anchor.relList?.supports?.("ar") ?? false;
}

/** Encode the optional banner into the USDZ URL fragment, per Apple's AR Quick Look scheme. */
export function buildQuickLookUrl(usdzUrl: string, banner?: QuickLookBanner): string {
  if (!banner) return usdzUrl;
  const params = new URLSearchParams();
  if (banner.callToAction) params.set("callToAction", banner.callToAction);
  if (banner.title) params.set("checkoutTitle", banner.title);
  if (banner.subtitle) params.set("checkoutSubtitle", banner.subtitle);
  if (banner.canonicalWebPageURL) params.set("canonicalWebPageURL", banner.canonicalWebPageURL);
  const fragment = params.toString();
  return fragment ? `${usdzUrl}#${fragment}` : usdzUrl;
}

/**
 * Launch AR Quick Look. The `<a rel="ar">` must contain a single child element to fire, and the
 * call must be inside a user gesture. Returns false if AQL isn't supported.
 */
export function presentQuickLook(usdzUrl: string, banner?: QuickLookBanner): boolean {
  if (!isQuickLookSupported()) return false;
  const anchor = document.createElement("a");
  anchor.setAttribute("rel", "ar");
  anchor.href = buildQuickLookUrl(usdzUrl, banner);
  anchor.appendChild(document.createElement("img")); // AQL requires one child node to launch
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
}
